import { useState, useEffect, useRef } from 'react';
import { Form, Modal, ModalContent, ModalHeader, ModalBody, Select, SelectItem, Button, Tabs, Tab, Input, Spinner } from '@nextui-org/react';
import { Toaster, toast } from 'react-hot-toast';
import Criterion from '../models/Criterion';
import { observer } from 'mobx-react-lite';

import { getTypedFormData } from '../utils/TypeFormData';

interface SplitSegmentModalProps {
  isFetching: boolean;
  isCreating: boolean;
  isOpen: boolean;
  criteria: Criterion[];
  onCriteriaAdd: (criterionIds: string[]) => void;
  onCriterionCreate: (name: string, prompt: string, callback: () => void) => void;
  onOpenChange: (open: boolean) => void;
}

type FormData = {
  name?: string;
  prompt?: string;
}

const SplitSegmentModal = observer((props: SplitSegmentModalProps) => {

  const {
    isOpen,
    onOpenChange,
    criteria,
    onCriterionCreate,
    isFetching,
    isCreating,
    onCriteriaAdd
  } = props;
  const [resetForm, setResetForm] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [selectedCriteriaKeys, setSelectedCriteriaKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (resetForm) {
      if (formRef.current) {
        formRef.current.reset();
      }
      setResetForm(false);
    }
  }, [resetForm]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = getTypedFormData<FormData>(event.currentTarget);
    const name = formData.get('name') || "";
    const prompt = formData.get('prompt') || "";
    if (name && prompt) {
      onCriterionCreate(name, prompt, () => {
        setResetForm(true);
        setSelectedCriteriaKeys(new Set());
      });
    } else {
      toast.error('Please fill in all fields');
    }
  }

  const handleSubmitAdd = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const criterionIds = Array.from(selectedCriteriaKeys);
    onCriteriaAdd(criterionIds);
  }

  return (
    <>
      <Toaster />
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="top-center"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Add Criterion</ModalHeader>
              <ModalBody>
                <Tabs aria-label="Options">
                  <Tab key="photos" title="Select Existing">
                    {isFetching ? (
                      <div className="flex justify-center items-center h-full">
                        <Spinner />
                      </div>
                    ) : (
                      <Form onSubmit={handleSubmitAdd} className="flex flex-col gap-2">
                        <Select
                          id="criterionIds"
                          name="criterionIds"
                          label="Criterion"
                          selectionMode="multiple"
                          selectedKeys={selectedCriteriaKeys}
                          onSelectionChange={(keys) => {
                            if (keys === "all") {
                              setSelectedCriteriaKeys(new Set(criteria.map(c => c.id)));
                            } else {
                              setSelectedCriteriaKeys(keys as Set<string>);
                            }
                          }}
                        >
                          {criteria.map((criterion) => (
                            <SelectItem key={criterion.id} value={criterion.id}>
                              {criterion.name}
                            </SelectItem>
                          ))}
                        </Select>
                        <div className="flex justify-end gap-2">
                          <Button type="button" onPress={onClose}>
                            Close
                          </Button>
                          <Button color="primary" type="submit">
                            Split
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Tab>
                  <Tab key="music" title="Create New">
                    <Form onSubmit={handleSubmit} className="flex flex-col gap-2" ref={formRef}>
                      <Input
                        id="name"
                        name="name"
                        label="Name"
                      />
                      <Input
                        id="prompt"
                        name="prompt"
                        label="Prompt"
                      />
                      <div className="flex justify-end gap-2">
                        <Button type="button" onPress={onClose}>
                          Close
                        </Button>
                        <Button color="primary" type="submit" isLoading={isCreating}>
                          Save
                        </Button>
                      </div>
                    </Form>
                  </Tab>
                </Tabs>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
});

export default SplitSegmentModal; 